import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  CircularProgress, 
  Typography, 
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { supabase, supabaseAdminClient } from '../../Supabase/supabaseRealtimeClient';
import { Medico } from '../../types/telemedicina';
import AddMedicoDialog from './AddMedicoDialog';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const MedicoList: React.FC = () => {
  const [medicos, setMedicos] = useState<Medico[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMedico, setSelectedMedico] = useState<Medico | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [medicoToDelete, setMedicoToDelete] = useState<Medico | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredMedicos = medicos.filter(medico => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      medico.nome?.toLowerCase().includes(query) ||
      medico.crm?.toLowerCase().includes(query) ||
      medico.uf_crm?.toLowerCase().includes(query) ||
      medico.especialidade?.toLowerCase().includes(query) ||
      medico.status?.toLowerCase().includes(query) ||
      (medico as any).email?.toLowerCase().includes(query)
    );
  });
  
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const fetchMedicos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('medicos')
        .select('*')
        .neq('deletado', true)
        .order('nome');

      if (error) throw error;
      setMedicos(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar médicos:', err);
      setError('Erro ao carregar lista de médicos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicos();

    const channel = supabase
      .channel('public:medicos')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'medicos' }, () => {
        fetchMedicos();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleEdit = (medico: Medico) => {
    setSelectedMedico(medico);
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedMedico(null);
    fetchMedicos(); // Refresh list just in case
  };

  const handleOpenDeleteDialog = (medico: Medico) => {
    setMedicoToDelete(medico);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMedicoToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!medicoToDelete) return;
    try {
      setLoading(true);

      // 1. Marcar como inativo e deletado na tabela medicos (Soft Delete)
      // Usamos adminClient se precisar
      const { error: deleteError } = await supabaseAdminClient
        .from('medicos')
        .update({ status: 'inativo', deletado: true })
        .eq('id', medicoToDelete.id);

      if (deleteError) throw deleteError;

      // 2. Agora que o registro foi inativado e "deletado", remove do Auth
      // Isso libera o e-mail para novos cadastros no Auth.
      if (medicoToDelete.user_id) {
        const { error: authError } = await supabaseAdminClient.auth.admin.deleteUser(medicoToDelete.user_id);
        
        if (authError) {
          console.error('Erro ao remover usuário do Auth:', authError);
          if (!authError.message.toLowerCase().includes('user not found')) {
            alert(`Médico removido do sistema, mas houve um erro ao liberar o e-mail no Auth: ${authError.message}`);
          }
        } else {
          alert('Médico excluído com sucesso!');
        }
      } else {
        alert('Médico excluído com sucesso!');
      }

      await fetchMedicos();
    } catch (err: any) {
      console.error('Erro no processo de exclusão:', err);
      alert(`Erro ao excluir médico: ${err.message}`);
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Pesquisar por nome, CRM, especialidade, status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="tabela de médicos">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CRM</TableCell>
              <TableCell>Especialidade</TableCell>
              <TableCell>Status</TableCell>
              {isDesktop && <TableCell>Data Cadastro</TableCell>}
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMedicos.length > 0 ? (
              filteredMedicos.map((medico) => (
                <TableRow key={medico.id}>
                  <TableCell>{medico.nome}</TableCell>
                  <TableCell>{medico.crm} / {medico.uf_crm || '-'}</TableCell>
                  <TableCell>{medico.especialidade}</TableCell>
                  <TableCell>
                    <Chip 
                      label={medico.status} 
                      color={medico.status === 'ativo' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  {isDesktop && (
                    <TableCell>
                      {new Date(medico.data_cadastro).toLocaleDateString()}
                    </TableCell>
                  )}
                  <TableCell>
                    <Button size="small" onClick={() => handleEdit(medico)}>
                      Editar
                    </Button>
                    <Button 
                      size="small" 
                      color="error"
                      onClick={() => handleOpenDeleteDialog(medico)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isDesktop ? 6 : 5} align="center">
                  Nenhum médico encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editDialogOpen && (
        <AddMedicoDialog 
          open={editDialogOpen} 
          onClose={handleCloseDialog} 
          medicoToEdit={selectedMedico}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        itemName={medicoToDelete?.nome}
      />
    </Box>
  );
};

export default MedicoList;
